import { useContext } from "react";
import { useNavigate } from "react-router";
import {
  Content,
  Text,
  Heading,
  ActionButtonGroup,
  ActionButton,
  Badge,
  InlineAlert,
} from "@react-spectrum/s2";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { DevToolbarItem } from "@app/views/projects/DevToolbar";
import { WIZARD_STEP } from "../wizardSteps";
import { Step } from "./Step";
import { TemplatesList } from "./TemplatesList";
import { AccountDetailsStep } from "./AccountDetailsStep";
import { ModularPlaceholderCard } from "./ModularPlaceholderCard";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

export const ModularTemplateStep = () => {
  const navigate = useNavigate();

  const {
    currentStep,
    templates,
    template,
    assetsTemplate,
    selectedTemplateKey,
    handleTemplateSelectionChange,
    handleTemplateContinue,
    handleNextStep,
    handleLink,
    colorScheme,
    sfType,
    setSfType,
    drNumbers,
    drNumbersFilter,
    setDrNumbersFilter,
    drNumber,
    handleDrNumbersSelectionChange,
    accountName,
    setAccountName,
  } = useContext(VideoWizardContext);

  return (
    <>
      <Step styles={style({ gridArea: "content" })}>
        <Step.Header
          title={
            <>
              <Text>Try out the new Video Composer</Text>
              <Badge
                size="XS"
                variant="yellow"
                styles={style({
                  marginStart: 8,
                })}
                UNSAFE_className={style({
                  verticalAlign: "text-top",
                  borderRadius: "sm",
                })}
                UNSAFE_style={{
                  paddingInlineStart: size(6),
                  paddingInlineEnd: size(6),
                  display: "inline-flex",
                }}
              >
                Alpha
              </Badge>
            </>
          }
          subtitle={
            <>
              <Text>
                Brand new script, unique video. Made for your Customer.
              </Text>
              <br />
              <Text>
                It&apos;s experimental — try it, break it, shape what&apos;s
                next.
              </Text>
            </>
          }
        >
          <ActionButtonGroup
            isQuiet
            staticColor="auto"
            styles={style({ marginTop: 32 })}
          >
            <ActionButton
              onPress={() => window.open("https://f.io/0NSot_uj", "_blank")}
            >
              Watch demo video
            </ActionButton>
            <ActionButton
              onPress={() => window.open("https://f.io/TxN9A3Q4", "_blank")}
            >
              See output examples
            </ActionButton>
            <ActionButton
              onPress={() =>
                window.open(
                  "slack://channel?team=E23RE8G4F&id=C095CSV0N0Z",
                  "_blank",
                )
              }
            >
              Share your thoughts
            </ActionButton>
          </ActionButtonGroup>
        </Step.Header>

        <TemplatesList
          templates={templates}
          selectedTemplateKey={selectedTemplateKey}
          onSelectionChange={handleTemplateSelectionChange}
          isDisabled={currentStep !== WIZARD_STEP.SELECT_TEMPLATE}
          placeholder={<ModularPlaceholderCard />}
        />
      </Step>

      <AccountDetailsStep
        isOpen={currentStep === WIZARD_STEP.ACCOUNT_DETAILS}
        sfType={sfType}
        onSfTypeChange={setSfType}
        accountName={accountName}
        onAccountNameChange={setAccountName}
        drNumbers={drNumbers}
        drNumbersFilter={drNumbersFilter}
        onDrNumbersFilterChange={setDrNumbersFilter}
        drNumber={drNumber}
        onDrNumbersSelectionChange={handleDrNumbersSelectionChange}
        onCancel={() => handleNextStep(WIZARD_STEP.SELECT_TEMPLATE)}
        onContinue={() => handleNextStep(WIZARD_STEP.CUSTOMER_DETAILS)}
      />

      {currentStep === WIZARD_STEP.SELECT_TEMPLATE && (
        <>
          {!!template.data?.disclaimer && (
            <div className={style({ gridArea: "info", justifySelf: "end" })}>
              <InlineAlert
                variant="neutral"
                styles={style({ width: 640, marginY: 24 })}
              >
                <Heading>{template.data.disclaimer.heading}</Heading>
                <Content>{template.data.disclaimer.content}</Content>
              </InlineAlert>
            </div>
          )}
        </>
      )}

      <DevToolbarItem
        isPending={templates.isFetching}
        onPress={() => templates.refetch()}
      >
        Refetch template
      </DevToolbarItem>
    </>
  );
};
